import {
  Box,
  Button,
  Grid,
  Typography,
  Card,
  CardContent,
  CardActions,
} from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";
import CodeIcon from "@mui/icons-material/Code";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import { Images } from "../../helpers/Images";

const CategoriesTemplate = () => {
  const navigate = useNavigate();

  const codeChampsData = {
    title: "CodeChamps",
    description: "CodeChamps is our premier competitive programming competition designed to challenge and inspire coders of all skill levels. Built for ambitious university students aged 5–17 years, it offers a platform to test problem-solving abilities and coding expertise through a series of challenging and engaging programming contests. Whether you're a beginner looking to sharpen your skills or an experienced programmer seeking intense challenges, CodeChamps is the perfect arena to showcase your talent.",
    details: [
      "Multiple difficulty levels from beginner to advanced",
      "Real-world problem-solving scenarios",
      "Online and offline competition formats available",
      "Comprehensive tracks including KidCoder, Web Challenge, Python Challenge, and AI Challenge",
      "Expert evaluation and detailed feedback",
      "Opportunities to compete with top programmers",
    ],
    link: "/codechamps",
    icon: <CodeIcon sx={{ fontSize: 60 }} />,
    backgroundImage: Images.code1,
  };

  const codePreneursData = {
    title: "CodePreneurs",
    description: "CodePreneurs is a unique competition that bridges the gap between coding skills and entrepreneurial thinking. Tailored for innovative university students aged 10–15 years, it challenges participants to develop not just technical solutions, but complete business models and market-ready products. Perfect for those eager to combine programming expertise with business acumen, CodePreneurs empowers participants to transform ideas into viable tech ventures.",
    details: [
      "Focus on innovation and market viability",
      "Business model development alongside technical solutions",
      "Mentorship from industry experts",
      "Pitch presentations and investor feedback",
      "Real-world startup scenarios",
      "Networking opportunities with entrepreneurs and investors",
    ],
    link: "/codepreneurs",
    icon: <BusinessCenterIcon sx={{ fontSize: 60 }} />,
    backgroundImage: Images.code2,
  };


  const codePioneersData = {
    title: "CodePioneers",
    description: "CodePioneers is a hands-on technical competition designed to bridge the gap between academic learning and real-world industry demands. Built for ambitious university students aged 18-24 years, it challenges participants to design, build, and pitch real AI-powered applications while gaining practical experience across the full lifecycle of modern tech solutions. Perfect for those looking to kickstart their careers in Artificial Intelligence and Data Science.",
    details: [
      "Intensive 3-day experience combining learning, building, and competing",
      "End-to-end AI application development using Python",
      "Practical workshop covering real industry tools and workflows",
      "Data-driven technical challenges based on real-world business scenarios",
      "Team-based competition with project pitching to expert judges",
      "Exposure to cutting-edge AI topics like LLMs, speech recognition, and image processing",
      "Career-boosting experience, cash prizes, and exclusive networking opportunities",
    ],
    link: "/codepioneers",
    icon: <BusinessCenterIcon sx={{ fontSize: 60 }} />,
    backgroundImage: Images.code3,
  };

  return (
    <Box
      id={"categories"}
      sx={{
        width: "100%",
        maxWidth: "1500px",
        margin: "auto",
        padding: { xs: "50px 20px", sm: "50px 60px", lg: "50px 120px" },
      }}
    >
      <Typography
        variant={"h1"}
        sx={{
          textAlign: "center",
          fontSize: { xs: "40px", sm: "55px" },
          fontWeight: "500",
          color: "primary.darker",
          marginBottom: "10px",
        }}
      >
        Code Challenge Events
      </Typography>
      <Box
        sx={{
          height: "3px",
          maxWidth: { xs: "100px", md: "300px" },
          width: "100%",
          backgroundColor: "primary.main",
          margin: "10px auto 30px auto",
        }}
      />

      <Grid
        container
        spacing={4}
        sx={{ marginTop: "20px", justifyContent: "center" }}
      >
        {/* CodeChamps Card */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              borderRadius: "20px",
              overflow: "hidden",
              boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
              transition: "all 0.3s ease-in-out",
              border: "1px solid rgba(106, 5, 5, 0.1)",
              "&:hover": {
                transform: "translateY(-8px)",
                boxShadow: "0 15px 50px rgba(106, 5, 5, 0.2)",
              },
            }}
          >
            <Box
              sx={{
                backgroundImage: `linear-gradient(135deg, rgba(106, 5, 5, 0.85), rgba(139, 13, 13, 0.75)), url(${codeChampsData.backgroundImage})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                padding: { xs: "30px 25px", sm: "40px 35px" },
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                position: "relative",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    "linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 100%)",
                  pointerEvents: "none",
                },
              }}
            >
              <Box
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  borderRadius: "50%",
                  width: { xs: "100px", sm: "120px" },
                  height: { xs: "100px", sm: "120px" },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "20px",
                  backdropFilter: "blur(10px)",
                  border: "2px solid rgba(255, 255, 255, 0.3)",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                {codeChampsData.icon}
              </Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "32px", sm: "38px" },
                  fontWeight: "700",
                  color: "secondary.main",
                  textAlign: "center",
                  marginBottom: "10px",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                {codeChampsData.title}
              </Typography>
              <Box
                sx={{
                  height: "3px",
                  width: "80px",
                  backgroundColor: "secondary.main",
                  borderRadius: "2px",
                  position: "relative",
                  zIndex: 1,
                }}
              />
            </Box>
            <CardContent
              sx={{
                flexGrow: 1,
                padding: { xs: "30px 25px", sm: "40px 35px" },
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "15px", sm: "17px" },
                  color: "primary.darker",
                  lineHeight: "1.8",
                  textAlign: "left",
                  marginBottom: "25px",
                }}
              >
                {codeChampsData.description}
              </Typography>
              <Box sx={{ marginBottom: "25px" }}>
                <Typography
                  sx={{
                    fontSize: { xs: "16px", sm: "18px" },
                    fontWeight: "600",
                    color: "primary.darker",
                    marginBottom: "15px",
                  }}
                >
                  Key Features:
                </Typography>
                {codeChampsData.details.map((detail, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      marginBottom: "10px",
                    }}
                  >
                    <Box
                      sx={{
                        width: "6px",
                        height: "6px",
                        borderRadius: "50%",
                        backgroundColor: "primary.main",
                        marginTop: "8px",
                        marginRight: "12px",
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: { xs: "14px", sm: "16px" },
                        color: "primary.darker",
                        lineHeight: "1.6",
                      }}
                    >
                      {detail}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
            <CardActions
              sx={{
                padding: { xs: "0 25px 30px", sm: "0 35px 40px" },
              }}
            >
              <Button
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                sx={{
                  fontSize: { xs: "15px", sm: "17px" },
                  fontWeight: "600",
                  color: "secondary.main",
                  padding: { xs: "12px 24px", sm: "14px 32px" },
                  textTransform: "none",
                  borderRadius: "10px",
                  boxShadow: "0 4px 15px rgba(106, 5, 5, 0.3)",
                  "&:hover": {
                    backgroundColor: "primary.light",
                    transform: "scale(1.02)",
                    boxShadow: "0 6px 20px rgba(106, 5, 5, 0.4)",
                  },
                  transition: "all 0.3s ease",
                }}
                onClick={() => navigate(codeChampsData.link)}
              >
                Explore CodeChamps
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Codepreneurs Card */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              borderRadius: "20px",
              overflow: "hidden",
              boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
              transition: "all 0.3s ease-in-out",
              border: "1px solid rgba(106, 5, 5, 0.1)",
              "&:hover": {
                transform: "translateY(-8px)",
                boxShadow: "0 15px 50px rgba(106, 5, 5, 0.2)",
              },
            }}
          >
            <Box
              sx={{
                backgroundImage: `linear-gradient(135deg, rgba(106, 5, 5, 0.85), rgba(139, 13, 13, 0.75)), url(${codePreneursData.backgroundImage})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                padding: { xs: "30px 25px", sm: "40px 35px" },
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                position: "relative",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    "linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 100%)",
                  pointerEvents: "none",
                },
              }}
            >
              <Box
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  borderRadius: "50%",
                  width: { xs: "100px", sm: "120px" },
                  height: { xs: "100px", sm: "120px" },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "20px",
                  backdropFilter: "blur(10px)",
                  border: "2px solid rgba(255, 255, 255, 0.3)",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                {codePreneursData.icon}
              </Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "32px", sm: "38px" },
                  fontWeight: "700",
                  color: "secondary.main",
                  textAlign: "center",
                  marginBottom: "10px",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                {codePreneursData.title}
              </Typography>
              <Box
                sx={{
                  height: "3px",
                  width: "80px",
                  backgroundColor: "secondary.main",
                  borderRadius: "2px",
                  position: "relative",
                  zIndex: 1,
                }}
              />
            </Box>
            <CardContent
              sx={{
                flexGrow: 1,
                padding: { xs: "30px 25px", sm: "40px 35px" },
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "15px", sm: "17px" },
                  color: "primary.darker",
                  lineHeight: "1.8",
                  textAlign: "left",
                  marginBottom: "25px",
                }}
              >
                {codePreneursData.description}
              </Typography>
              <Box sx={{ marginBottom: "25px" }}>
                <Typography
                  sx={{
                    fontSize: { xs: "16px", sm: "18px" },
                    fontWeight: "600",
                    color: "primary.darker",
                    marginBottom: "15px",
                  }}
                >
                  Key Features:
                </Typography>
                {codePreneursData.details.map((detail, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      marginBottom: "10px",
                    }}
                  >
                    <Box
                      sx={{
                        width: "6px",
                        height: "6px",
                        borderRadius: "50%",
                        backgroundColor: "primary.main",
                        marginTop: "8px",
                        marginRight: "12px",
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: { xs: "14px", sm: "16px" },
                        color: "primary.darker",
                        lineHeight: "1.6",
                      }}
                    >
                      {detail}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
            <CardActions
              sx={{
                padding: { xs: "0 25px 30px", sm: "0 35px 40px" },
              }}
            >
              <Button
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                sx={{
                  fontSize: { xs: "15px", sm: "17px" },
                  fontWeight: "600",
                  color: "secondary.main",
                  padding: { xs: "12px 24px", sm: "14px 32px" },
                  textTransform: "none",
                  borderRadius: "10px",
                  boxShadow: "0 4px 15px rgba(106, 5, 5, 0.3)",
                  "&:hover": {
                    backgroundColor: "primary.light",
                    transform: "scale(1.02)",
                    boxShadow: "0 6px 20px rgba(106, 5, 5, 0.4)",
                  },
                  transition: "all 0.3s ease",
                }}
                onClick={() => navigate(codePreneursData.link)}
              >
                Explore Codepreneurs
              </Button>
            </CardActions>
          </Card>
        </Grid>


        {/* CodePioneers Card */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              borderRadius: "20px",
              overflow: "hidden",
              boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
              transition: "all 0.3s ease-in-out",
              border: "1px solid rgba(106, 5, 5, 0.1)",
              "&:hover": {
                transform: "translateY(-8px)",
                boxShadow: "0 15px 50px rgba(106, 5, 5, 0.2)",
              },
            }}
          >
            <Box
              sx={{
                backgroundImage: `linear-gradient(135deg, rgba(106, 5, 5, 0.85), rgba(139, 13, 13, 0.75)), url(${codePreneursData.backgroundImage})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                padding: { xs: "30px 25px", sm: "40px 35px" },
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                position: "relative",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    "linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 100%)",
                  pointerEvents: "none",
                },
              }}
            >
              <Box
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  borderRadius: "50%",
                  width: { xs: "100px", sm: "120px" },
                  height: { xs: "100px", sm: "120px" },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "20px",
                  backdropFilter: "blur(10px)",
                  border: "2px solid rgba(255, 255, 255, 0.3)",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                {codePreneursData.icon}
              </Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "32px", sm: "38px" },
                  fontWeight: "700",
                  color: "secondary.main",
                  textAlign: "center",
                  marginBottom: "10px",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                {codePioneersData.title}
              </Typography>
              <Box
                sx={{
                  height: "3px",
                  width: "80px",
                  backgroundColor: "secondary.main",
                  borderRadius: "2px",
                  position: "relative",
                  zIndex: 1,
                }}
              />
            </Box>
            <CardContent
              sx={{
                flexGrow: 1,
                padding: { xs: "30px 25px", sm: "40px 35px" },
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "15px", sm: "17px" },
                  color: "primary.darker",
                  lineHeight: "1.8",
                  textAlign: "left",
                  marginBottom: "25px",
                }}
              >
                {codePioneersData.description}
              </Typography>
              <Box sx={{ marginBottom: "25px" }}>
                <Typography
                  sx={{
                    fontSize: { xs: "16px", sm: "18px" },
                    fontWeight: "600",
                    color: "primary.darker",
                    marginBottom: "15px",
                  }}
                >
                  Key Features:
                </Typography>
                {codePioneersData.details.map((detail, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      marginBottom: "10px",
                    }}
                  >
                    <Box
                      sx={{
                        width: "6px",
                        height: "6px",
                        borderRadius: "50%",
                        backgroundColor: "primary.main",
                        marginTop: "8px",
                        marginRight: "12px",
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: { xs: "14px", sm: "16px" },
                        color: "primary.darker",
                        lineHeight: "1.6",
                      }}
                    >
                      {detail}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
            <CardActions
              sx={{
                padding: { xs: "0 25px 30px", sm: "0 35px 40px" },
              }}
            >
              <Button
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                sx={{
                  fontSize: { xs: "15px", sm: "17px" },
                  fontWeight: "600",
                  color: "secondary.main",
                  padding: { xs: "12px 24px", sm: "14px 32px" },
                  textTransform: "none",
                  borderRadius: "10px",
                  boxShadow: "0 4px 15px rgba(106, 5, 5, 0.3)",
                  "&:hover": {
                    backgroundColor: "primary.light",
                    transform: "scale(1.02)",
                    boxShadow: "0 6px 20px rgba(106, 5, 5, 0.4)",
                  },
                  transition: "all 0.3s ease",
                }}
                onClick={() => navigate(codePioneersData.link)}
              >
                Explore CodePioneers
              </Button>
            </CardActions>
          </Card>
        </Grid>

      </Grid>
    </Box>
  );
};

export default CategoriesTemplate;
